import {
  action,
  makeObservable,
  observable,
  reaction,
  runInAction,
  toJS,
} from 'mobx';

import {delay} from '@yourcoach/async-utils';

import type {
  CoreClientMembershipsAcceptParams,
  CoreClientMembershipsChangePaymentMethodParams,
  CoreClientMembershipsCreateParams,
  CoreClientMembershipsDeclineParams,
  CoreClientMembershipsFreezeParams,
  CoreClientMembershipsListParams,
  CoreClientMembershipsReadParams,
  CoreClientMembershipsRequestRefundParams,
  CoreCoachMembershipsAcceptParams,
  CoreCoachMembershipsCreateParams,
  CoreCoachMembershipsDeclineParams,
  CoreCoachMembershipsFreezeParams,
  CoreCoachMembershipsMoveParams,
  CoreCoachMembershipsMoveToEditionParams,
  CoreCoachMembershipsReadParams,
  CoreCoachMembershipsUnfreezeParams,
} from '../types/core/methods';
import type {MethodStatus} from '../utils/entityStore';
import type {MembershipApiModel} from './types';

import {envManager} from '../../utils/env';
import {emitter, WS_RECEIVE_MESSAGE_EVENT} from '../../utils/eventEmitter';
import {storage} from '../../utils/storage';
import {isTestEnv} from '../../utils/test-utils/isTestEnv';
import {currentUserStore} from '../user';
import {apiRequest} from '../utils/apiRequest';
import {EntityStore} from '../utils/entityStore';

export const STORAGE_KEY_MEMBERSHIP = 'memberships';

const STORED_MEMBERSHIP_STATUSES = [
  'accepted',
  'active',
  'archived',
  'frozen',
  'requested',
  'proposed',
] as MembershipApiModel['status'][];

export type CleanedMembership = Pick<
  MembershipApiModel,
  | '_id'
  | 'card_id'
  | 'course_id'
  | 'edition_id'
  | 'frozen_by'
  | 'program_id'
  | 'requested_refund'
  | 'status'
>;

class ClientMembershipStore<
  T extends MembershipApiModel = MembershipApiModel,
> extends EntityStore<
  T,
  CoreClientMembershipsReadParams,
  CoreClientMembershipsCreateParams
> {
  accept = this._createMethod<CoreClientMembershipsAcceptParams>({
    method: 'client.memberships.accept',
    onSuccess: membership => {
      runInAction(() => {
        this._options.addMembershipIfNeeded(membership);
      });
    },
  });
  acceptBatch = this._createBatchMethod<CoreClientMembershipsAcceptParams>({
    method: 'client.memberships.accept',
    onSuccess: memberships => {
      memberships.forEach(membership => {
        runInAction(() => {
          this._options.addMembershipIfNeeded(membership);
        });
      });
    },
  });
  changePaymentMethod =
    this._createMethod<CoreClientMembershipsChangePaymentMethodParams>({
      method: 'client.memberships.change_payment_method',
    });
  declining: MethodStatus<T> = {
    entity: null,
    error: null,
    inProgress: false,
    success: false,
  };
  decline = this._createMethod<CoreClientMembershipsDeclineParams>({
    method: 'client.memberships.decline',
    onBefore: entity => {
      runInAction(() => {
        this.declining = {
          entity,
          error: null,
          inProgress: true,
          success: false,
        };
      });
    },
    onFail: (entity, error) => {
      runInAction(() => {
        this.declining = {
          entity,
          error,
          inProgress: false,
          success: false,
        };
      });
    },
    onSuccess: entity => {
      runInAction(() => {
        this.declining = {
          entity,
          error: null,
          inProgress: false,
          success: true,
        };

        this._options.removeMembership(entity);
      });
    },
  });
  freeze = this._createMethod<CoreClientMembershipsFreezeParams>({
    method: 'client.memberships.freeze',
    onSuccess: membership => {
      runInAction(() => {
        this._options.addMembershipIfNeeded(membership);
      });
    },
  });
  freezeBatch = this._createBatchMethod<CoreClientMembershipsFreezeParams>({
    method: 'client.memberships.freeze',
    onSuccess: memberships => {
      memberships.forEach(membership => {
        runInAction(() => {
          this._options.addMembershipIfNeeded(membership);
        });
      });
    },
  });
  requestRefund = this._createMethod<CoreClientMembershipsRequestRefundParams>({
    method: 'client.memberships.request_refund',
    onSuccess: membership => {
      runInAction(() => {
        this._options.addMembershipIfNeeded(membership);
      });
    },
  });

  constructor(...args: ConstructorParameters<typeof EntityStore>) {
    super(...args);

    makeObservable(this, {
      declining: observable,
    });
  }

  async declineBatch(
    items: {
      membership: T;
      params?: Omit<CoreClientMembershipsDeclineParams, '_id'>;
    }[],
  ) {
    await apiRequest<CoreClientMembershipsDeclineParams>({
      batch: items.map(item => ({
        method: 'client.memberships.decline',
        params: {
          ...item.params,
          _id: item.membership._id,
        },
      })),
    });

    items.forEach(item => {
      runInAction(() => {
        this._options.removeMembership(item.membership);
      });
    });

    return items.map(() => null);
  }
}

class CoachMembershipStore<
  T extends MembershipApiModel = MembershipApiModel,
> extends EntityStore<
  T,
  CoreCoachMembershipsReadParams,
  CoreCoachMembershipsCreateParams
> {
  accept = this._createMethod<CoreCoachMembershipsAcceptParams>({
    method: 'coach.memberships.accept',
  });
  acceptBatch = this._createBatchMethod<CoreCoachMembershipsAcceptParams>({
    method: 'coach.memberships.accept',
  });
  freeze = this._createMethod<CoreCoachMembershipsFreezeParams>({
    method: 'coach.memberships.freeze',
  });
  freezeBatch = this._createBatchMethod<CoreCoachMembershipsFreezeParams>({
    method: 'coach.memberships.freeze',
  });

  async decline(
    membership: T,
    params?: Omit<CoreCoachMembershipsDeclineParams, '_id'>,
  ) {
    await apiRequest<CoreCoachMembershipsDeclineParams>({
      method: 'coach.memberships.decline',
      params: {
        ...params,
        _id: membership._id,
      },
    });

    return null;
  }

  async declineBatch(
    items: {
      membership: T;
      params?: Omit<CoreCoachMembershipsDeclineParams, '_id'>;
    }[],
  ) {
    await apiRequest<CoreCoachMembershipsDeclineParams>({
      batch: items.map(item => ({
        method: 'coach.memberships.decline',
        params: {
          ...item.params,
          _id: item.membership._id,
        },
      })),
    });

    return items.map(() => null);
  }

  async moveToEdition(params: CoreCoachMembershipsMoveToEditionParams) {
    await apiRequest({
      method: 'coach.memberships.move_to_edition',
      params,
    });
  }
}

export class MembershipStore<
  T extends MembershipApiModel = MembershipApiModel,
> extends EntityStore<T> {
  client = new ClientMembershipStore<T>(
    'membership',
    {
      create: 'client.memberships.create',
      fetch: 'client.memberships.read',
    },
    {
      addMembershipIfNeeded: this.addMembershipIfNeeded.bind(this),
      removeMembership: this.removeMembership.bind(this),
    },
  );
  coach = new CoachMembershipStore<T>('membership', {
    create: 'coach.memberships.create',
    delete: 'coach.memberships.delete',
    fetch: 'coach.memberships.read',
  });
  membershipLookup = new Map<string, CleanedMembership>();
  memberships: T[] = [];
  move = this._createMethod<CoreCoachMembershipsMoveParams>({
    method: 'coach.memberships.move',
  });
  moveBatch = this._createBatchMethod<CoreCoachMembershipsMoveParams>({
    method: 'coach.memberships.move',
  });
  unfreeze = this._createMethod<CoreCoachMembershipsUnfreezeParams>({
    method: 'coach.memberships.unfreeze',
  });
  unfreezeBatch = this._createBatchMethod<CoreCoachMembershipsUnfreezeParams>({
    method: 'coach.memberships.unfreeze',
  });

  constructor(...args: ConstructorParameters<typeof EntityStore>) {
    super(...args);

    this.addMembershipIfNeeded = this.addMembershipIfNeeded.bind(this);
    this.removeMembership = this.removeMembership.bind(this);
    this.onWsMessage = this.onWsMessage.bind(this);

    makeObservable(this, {
      addMembershipIfNeeded: !isTestEnv ? action.bound : false,
      membershipLookup: observable.shallow,
      memberships: observable,
      removeMembership: !isTestEnv ? action.bound : false,
    });

    reaction(
      () => this.memberships,
      () => {
        this.membershipLookup.clear();

        this.memberships.map(this._cleanupMembership).forEach(membership => {
          const key = membership.course_id || membership.program_id;

          if (key) {
            this.membershipLookup.set(key, membership);
          }
        });

        storage.setItem(
          this.getStorageKey(),
          JSON.stringify(toJS(this.memberships)),
        );
      },
    );

    reaction(
      () =>
        envManager.isReady &&
        currentUserStore.user &&
        currentUserStore.user._id,
      () => {
        if (!envManager.isReady) {
          return;
        }

        if (!currentUserStore.user) {
          runInAction(() => {
            this.memberships = [];

            this.membershipLookup.clear();

            storage.removeItem(this.getStorageKey());
          });
        } else {
          this.fetchMemberships();
        }
      },
      {fireImmediately: true},
    );

    reaction(
      () => this.client.creating,
      creating => {
        if (creating.success) {
          this.addMembershipIfNeeded(creating.entity!);
        }
      },
    );

    emitter.on(WS_RECEIVE_MESSAGE_EVENT, this.onWsMessage);
  }

  _cleanupMembership(membership: MembershipApiModel): CleanedMembership {
    return {
      _id: membership._id,
      card_id: membership.card_id,
      course_id: membership.course_id,
      edition_id: membership.edition_id,
      frozen_by: membership.frozen_by,
      program_id: membership.program_id,
      requested_refund: membership.requested_refund,
      status: membership.status,
    };
  }

  async _restoreMemberships() {
    try {
      if (this.memberships.length) {
        return true;
      }

      const memberships = JSON.parse(
        await storage.getItem(this.getStorageKey()),
      ) as T[];

      if (memberships) {
        runInAction(() => {
          this.memberships = memberships;

          this.fetchMemberships();
        });

        return true;
      }

      return false;
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        // eslint-disable-next-line no-console
        console.warn('Error restoring memberships', error);
      }

      storage.removeItem(this.getStorageKey());

      return false;
    }
  }

  addMembershipIfNeeded(membership: MembershipApiModel) {
    if (
      currentUserStore.user &&
      membership.user_id === currentUserStore.user._id &&
      STORED_MEMBERSHIP_STATUSES.includes(membership.status)
    ) {
      const key = membership.course_id || membership.program_id;

      if (key) {
        this.membershipLookup.set(key, this._cleanupMembership(membership));
      }

      this.fetchMemberships();
    }
  }

  async decline(
    membership: T,
    params?: Omit<CoreCoachMembershipsDeclineParams, '_id'>,
  ) {
    await apiRequest<CoreCoachMembershipsDeclineParams>({
      method: 'coach.memberships.decline',
      params: {
        ...params,
        _id: membership._id,
      },
    });

    return null;
  }

  async declineBatch(
    items: {
      membership: T;
      params?: Omit<CoreCoachMembershipsDeclineParams, '_id'>;
    }[],
  ) {
    await apiRequest<CoreCoachMembershipsDeclineParams>({
      batch: items.map(item => ({
        method: 'coach.memberships.decline',
        params: {
          ...item.params,
          _id: item.membership._id,
        },
      })),
    });

    return items.map(() => null);
  }

  async fetchMemberships() {
    try {
      if (!currentUserStore.user) {
        return;
      }

      this.fetching = {
        error: null,
        inProgress: true,
        success: false,
      };

      const result = await apiRequest<CoreClientMembershipsListParams>({
        method: 'client.memberships.list',
        params: {
          limit: 1000,
          query: [
            ['status', 'in', STORED_MEMBERSHIP_STATUSES],
            ['user_id', '==', currentUserStore.user._id],
          ],
          sort: [['created', -1]],
        },
      });

      runInAction(() => {
        this.memberships = result._items;

        this.fetching = {
          error: null,
          inProgress: false,
          success: true,
        };
      });
    } catch (error) {
      this.fetching = {
        error,
        inProgress: false,
        success: false,
      };
      await delay(2000);
      await this.fetchMemberships();
    }
  }

  getStorageKey() {
    const prefix = envManager.get('isB2b') ? 'b2b_' : '';

    return `${prefix}${STORAGE_KEY_MEMBERSHIP}`;
  }

  removeMembership(membership: MembershipApiModel) {
    const key = membership.course_id || membership.program_id;

    if (key) {
      this.membershipLookup.delete(key);
    }
  }

  private onWsMessage(message: any) {
    if (
      [
        'membership_accepted',
        'membership_frozen',
        'membership_proposed',
        'membership_unfrozen',
      ].includes(message.event.type)
    ) {
      this.addMembershipIfNeeded(message.membership);
    }

    if (
      [
        'membership_declined', // coach has declined a request
        'membership_expired', // a coach has changed program conditions
        'membership_revoked', // a coach has declined an invitation
      ].includes(message.event.type)
    ) {
      const key = message.event.course?._id || message.event.program?._id;

      if (key) {
        this.membershipLookup.delete(key);
      }
    }

    if (message.event.type.includes('membership')) {
      // refresh memberships just in case
      this.fetchMemberships();
    }
  }
}

export const membershipStore = new MembershipStore('membership');
